/**
 * Chat API — TanStack Query hooks + low-level fetchers for every endpoint
 * the new chat backend exposes.
 *
 * # Endpoint shapes
 *
 *   POST    /chat/conversations                  create
 *   GET     /chat/conversations                  list (paginated + filters)
 *   GET     /chat/conversations/{id}             detail
 *   PATCH   /chat/conversations/{id}             update
 *   DELETE  /chat/conversations/{id}             delete
 *   GET     /chat/conversations/{id}/history     full transcript
 *
 *   POST    /chat/conversations/{id}/messages    kickoff → AgentRunAck (202)
 *   GET     /chat/runs/{run_id}/events           SSE stream (resumable)
 *   DELETE  /chat/runs/{run_id}                  best-effort abort
 *   GET     /chat/active                         owner-scoped active runs
 *
 *   GET     /chat/models                         live LiteLLM proxy registry
 *
 *   PATCH   /chat/conversations/{id}/turns/{tid}/context-view
 *
 * # Two SSE attach paths, one transport (sse-client.streamGetSSE)
 *
 *   - send-attach: POST kickoff → server returns run_id → GET /runs/{run_id}/events
 *   - resume:      GET /runs/{run_id}/events?last_event_id=N from cold start
 *
 * Both flows end up calling resumeRunEvents() below — that's the single
 * point where SSE attachment happens. Callers in chat-stream-store own
 * the per-conversation lifecycle (kickoff, attach, abort, cleanup).
 */
"use client";

import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiClient, type PaginatedResponse } from "@/shared/lib/api/client";
import {
  type SSEGetHandle,
  streamGetSSE,
} from "@/shared/lib/api/sse-client";

import type {
  ActiveRun,
  ActiveRunsResponse,
  AgentEvent,
  AgentRunAck,
  ChatMessageRequest,
  Conversation,
  ConversationCreate,
  ConversationHistory,
  ConversationListItem,
  ConversationUpdate,
  ModelInfo,
  ModelsResponse,
  RunAbortResponse,
} from "../types";

// ====================================================================
// Query-key factory
// ====================================================================

export interface ConversationListParams {
  skip?: number;
  limit?: number;
  include_archived?: boolean;
  in_grid?: boolean;
  grid_label?: string;
}

export const chatKeys = {
  all: ["chat"] as const,
  conversations: () => [...chatKeys.all, "conversations"] as const,
  conversationList: (params?: ConversationListParams) =>
    [...chatKeys.conversations(), "list", params ?? {}] as const,
  conversation: (id: string) =>
    [...chatKeys.conversations(), "detail", id] as const,
  history: (id: string) =>
    [...chatKeys.conversations(), "history", id] as const,
  active: () => [...chatKeys.all, "active"] as const,
  models: (mode?: string) => [...chatKeys.all, "models", mode] as const,
};

// ====================================================================
// Internal — query-string serializer
// ====================================================================

function listQueryString(params: ConversationListParams = {}): string {
  const search = new URLSearchParams();
  search.set("skip", String(params.skip ?? 0));
  search.set("limit", String(params.limit ?? 50));
  search.set("include_archived", String(params.include_archived ?? false));
  if (params.in_grid !== undefined) search.set("in_grid", String(params.in_grid));
  if (params.grid_label !== undefined) search.set("grid_label", params.grid_label);
  return `?${search.toString()}`;
}

// ====================================================================
// Conversation reads
// ====================================================================

export function useConversations(params?: ConversationListParams) {
  return useQuery({
    queryKey: chatKeys.conversationList(params),
    queryFn: () =>
      apiClient<PaginatedResponse<ConversationListItem>>(
        `/api/chat/conversations${listQueryString(params)}`,
      ),
    staleTime: 30_000,
  });
}

export function useConversation(id: string) {
  return useQuery({
    queryKey: chatKeys.conversation(id),
    queryFn: () => apiClient<Conversation>(`/api/chat/conversations/${id}`),
    enabled: !!id,
    staleTime: 60_000,
  });
}

/**
 * Single source-of-truth fetcher for one conversation's history.
 * Both the React hook (``useConversationHistory``) and the
 * subscription manager's prefetch path call this, so they can never
 * drift on headers, error shape, or URL construction.
 */
export function fetchConversationHistory(
  id: string,
): Promise<ConversationHistory> {
  return apiClient<ConversationHistory>(
    `/api/chat/conversations/${id}/history`,
  );
}

export function useConversationHistory(id: string) {
  return useQuery({
    queryKey: chatKeys.history(id),
    queryFn: () => fetchConversationHistory(id),
    enabled: !!id,
    // Rely on explicit invalidation from the completion handoff in
    // chat-stream-store; navigation refetches via cache miss otherwise.
  });
}

// ====================================================================
// Conversation writes
// ====================================================================

/**
 * Stable optimistic-id prefix. The grid uses this to detect optimistic
 * rows and render their "submitting" state without a refetch.
 */
export const OPTIMISTIC_CONVERSATION_PREFIX = "optimistic-";

export function isOptimisticConversationId(id: string): boolean {
  return id.startsWith(OPTIMISTIC_CONVERSATION_PREFIX);
}

/**
 * Mint a fresh optimistic conversation id. Callers that want to track
 * their own optimistic row across the mutation lifecycle (the agents
 * spawn hook, for example) generate the id ahead of time and pass it
 * to ``useCreateConversation`` via the ``__optimisticId`` input field —
 * deterministic, no cache walks, no concurrent-spawn races.
 */
export function mintOptimisticConversationId(): string {
  // Date.now() gives chronological ordering for free; the random suffix
  // covers same-millisecond collisions when N spawns fire from a batch.
  return `${OPTIMISTIC_CONVERSATION_PREFIX}${Date.now()}-${Math.random()
    .toString(36)
    .slice(2, 8)}`;
}

/**
 * Input shape for ``useCreateConversation`` — extends the wire schema
 * with a private ``__optimisticId`` field the mutation uses for the
 * optimistic row's stable id. Stripped before serialization so it
 * never reaches the server.
 */
export interface CreateConversationInput extends ConversationCreate {
  __optimisticId?: string;
}

function emptyOptimisticListItem(
  input: CreateConversationInput,
  optimisticId: string,
): ConversationListItem {
  const now = new Date().toISOString();
  return {
    id: optimisticId,
    title: input.title ?? null,
    owner_id: null,
    default_model: input.default_model ?? null,
    is_archived: false,
    in_grid: input.in_grid ?? false,
    grid_label: input.grid_label ?? null,
    turn_count: 0,
    last_output_excerpt: null,
    created_at: now,
    updated_at: now,
  };
}

function toListItem(c: Conversation): ConversationListItem {
  return {
    id: c.id,
    title: c.title,
    owner_id: c.owner_id,
    default_model: c.default_model,
    is_archived: c.is_archived,
    in_grid: c.in_grid,
    grid_label: c.grid_label,
    turn_count: c.turn_count,
    last_output_excerpt: null,
    created_at: c.created_at,
    updated_at: c.updated_at,
  };
}

/**
 * Predicate for "is this query key a conversations-list query whose
 * filters match the optimistic row we're about to insert?"
 *
 * The grid view's list key is ``["chat","conversations","list",{in_grid:true,grid_label:…,limit:100}]``;
 * the sidebar's is ``["chat","conversations","list",{}]``; both must
 * receive the optimistic row so the new cell appears in the grid
 * INSTANTLY and shows up in the sidebar at the same time.
 *
 * We pessimistically include any list query that doesn't explicitly
 * filter the new row OUT (e.g. ``in_grid:false`` excludes an
 * ``in_grid:true`` row). When in doubt, include — the next refetch
 * fixes any over-inclusion.
 */
function matchesOptimisticRow(
  queryKey: readonly unknown[],
  input: ConversationCreate,
): boolean {
  if (queryKey[0] !== "chat") return false;
  if (queryKey[1] !== "conversations") return false;
  if (queryKey[2] !== "list") return false;
  const params = (queryKey[3] ?? {}) as ConversationListParams;
  if (params.in_grid === true && input.in_grid !== true) return false;
  if (params.in_grid === false && input.in_grid === true) return false;
  if (
    params.grid_label !== undefined &&
    params.grid_label !== input.grid_label
  ) {
    return false;
  }
  return true;
}

export function useCreateConversation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateConversationInput) => {
      // Strip the client-side correlator before sending — the server
      // doesn't know or care about it.
      const { __optimisticId: _stripped, ...wire } = input;
      void _stripped;
      return apiClient<Conversation>("/api/chat/conversations", {
        method: "POST",
        body: JSON.stringify(wire),
      });
    },

    onMutate: async (input) => {
      // Cancel any in-flight matching queries so our optimistic write
      // isn't immediately overwritten by a stale fetch.
      await queryClient.cancelQueries({ queryKey: chatKeys.conversations() });

      // Snapshot every matching list query so onError can restore them all.
      const previous = queryClient.getQueriesData<
        PaginatedResponse<ConversationListItem>
      >({
        predicate: (q) => matchesOptimisticRow(q.queryKey, input),
      });

      // Caller-provided id wins (the agents grid passes its own so it
      // can track the cell deterministically across the lifecycle).
      // Without one, mint a fresh id. Either way the same id is used
      // by both onMutate's insert and onSuccess's swap.
      const optimisticId =
        input.__optimisticId ?? mintOptimisticConversationId();
      const optimistic = emptyOptimisticListItem(input, optimisticId);

      queryClient.setQueriesData<PaginatedResponse<ConversationListItem>>(
        { predicate: (q) => matchesOptimisticRow(q.queryKey, input) },
        (old) => {
          const items = old?.items ?? [];
          return {
            items: [optimistic, ...items],
            total: (old?.total ?? 0) + 1,
            skip: old?.skip ?? 0,
            limit: old?.limit ?? 50,
            has_more: old?.has_more ?? false,
          };
        },
      );

      return { previous, optimisticId, input };
    },

    onSuccess: (conversation, input, context) => {
      // Swap the optimistic row for the real one in every list query
      // that received the optimistic insert. toListItem() preserves the
      // grid metadata from the input so the cell stays visible without
      // a refetch.
      queryClient.setQueriesData<PaginatedResponse<ConversationListItem>>(
        { predicate: (q) => matchesOptimisticRow(q.queryKey, input) },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            items: old.items.map((c) =>
              c.id === context?.optimisticId
                ? toListItem(conversation)
                : c,
            ),
          };
        },
      );
      queryClient.setQueryData(
        chatKeys.conversation(conversation.id),
        conversation,
      );
    },

    onError: (_err, _input, context) => {
      // Restore every snapshot we took on onMutate.
      for (const [key, data] of context?.previous ?? []) {
        queryClient.setQueryData(key, data);
      }
    },
  });
}

export function useUpdateConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ConversationUpdate }) =>
      apiClient<Conversation>(`/api/chat/conversations/${id}`, {
        method: "PATCH",
        body: JSON.stringify(data),
      }),
    onSuccess: (conversation) => {
      queryClient.setQueryData(
        chatKeys.conversation(conversation.id),
        conversation,
      );
      queryClient.invalidateQueries({ queryKey: chatKeys.conversations() });
    },
  });
}

export function useDeleteConversation() {
  const queryClient = useQueryClient();
  const listKey = chatKeys.conversationList();

  return useMutation({
    mutationFn: (id: string) =>
      apiClient<void>(`/api/chat/conversations/${id}`, { method: "DELETE" }),

    onMutate: async (deletedId) => {
      await queryClient.cancelQueries({ queryKey: listKey });
      const previous = queryClient.getQueryData<
        PaginatedResponse<ConversationListItem>
      >(listKey);
      queryClient.setQueryData<PaginatedResponse<ConversationListItem>>(
        listKey,
        (old) =>
          old
            ? {
                ...old,
                items: old.items.filter((c) => c.id !== deletedId),
                total: Math.max(0, old.total - 1),
              }
            : old,
      );
      return { previous };
    },

    onSuccess: (_data, deletedId) => {
      queryClient.removeQueries({ queryKey: chatKeys.conversation(deletedId) });
      queryClient.removeQueries({ queryKey: chatKeys.history(deletedId) });
    },

    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(listKey, context.previous);
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: chatKeys.conversations() });
    },
  });
}

// ====================================================================
// Run kickoff + lifecycle (the new contract)
// ====================================================================

/**
 * Kick off an agent run. Returns once the job is durably enqueued — the
 * agent will run to completion regardless of whether the client subscribes.
 *
 * This is a one-shot fetch, not a hook — the caller (chat-stream-store)
 * needs the run_id synchronously to attach the SSE stream and update its
 * own in-memory state.
 */
export async function kickoffAgentRun(
  conversationId: string,
  request: ChatMessageRequest,
): Promise<AgentRunAck> {
  return apiClient<AgentRunAck>(
    `/api/chat/conversations/${conversationId}/messages`,
    {
      method: "POST",
      body: JSON.stringify(request),
    },
  );
}

/**
 * Attach an SSE subscription to a run. Returns a handle whose
 * ``close()`` aborts the underlying fetch; ``done`` resolves when the
 * stream completes.
 *
 * Always starts at ``last_event_id=0-0`` — the wire layer doesn't
 * expose Redis Stream entry ids to ``onEvent``, so there's no honest
 * cursor to resume from. The backend's content_parts replay on
 * conversation history fetch is the authoritative resume mechanism;
 * see chat-stream-store header for the design.
 */
export function resumeRunEvents(
  runId: string,
  onEvent: (ev: AgentEvent) => void,
  options: { onError?: (err: Error) => void } = {},
): SSEGetHandle {
  return streamGetSSE<AgentEvent>(
    `/api/chat/runs/${runId}/events?last_event_id=0-0`,
    onEvent,
    options.onError,
  );
}

/**
 * Best-effort abort. Returns ``{aborted: false}`` if the run wasn't in the
 * active index (already finished, expired, or never existed) — not an error.
 */
export async function abortAgentRun(runId: string): Promise<RunAbortResponse> {
  return apiClient<RunAbortResponse>(`/api/chat/runs/${runId}`, {
    method: "DELETE",
  });
}

// ====================================================================
// Active runs index
// ====================================================================

const ACTIVE_POLL_INTERVAL_MS = 10_000;

export function useActiveRuns() {
  return useQuery({
    queryKey: chatKeys.active(),
    queryFn: () => apiClient<ActiveRunsResponse>("/api/chat/active"),
    // Cheap Redis call; poll so the AppHeader pulse and grid spinners stay
    // current even when no SSE is open in this tab. Stops when the tab
    // backgrounds (TanStack default).
    refetchInterval: ACTIVE_POLL_INTERVAL_MS,
    staleTime: ACTIVE_POLL_INTERVAL_MS,
  });
}

export async function fetchActiveRuns(): Promise<ActiveRun[]> {
  const res = await apiClient<ActiveRunsResponse>("/api/chat/active");
  return res.runs;
}

// ====================================================================
// Models
// ====================================================================

export function useModels(mode: string = "chat") {
  return useQuery({
    queryKey: chatKeys.models(mode),
    queryFn: () => apiClient<ModelsResponse>(`/api/chat/models?mode=${mode}`),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

export function useModelsGroupedByProvider(mode: string = "chat") {
  const { data, ...rest } = useModels(mode);
  const models = data?.models;
  const groupedModels = useMemo(() => {
    if (!models) return undefined;
    return models.reduce(
      (acc, model) => {
        if (!acc[model.provider]) acc[model.provider] = [];
        acc[model.provider].push(model);
        return acc;
      },
      {} as Record<string, ModelInfo[]>,
    );
  }, [models]);
  return { ...rest, data: models, groupedModels, total: data?.total ?? 0 };
}

// ====================================================================
// Context-view editing
// ====================================================================

export function useUpdateContextView() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      conversationId,
      turnId,
      contextView,
    }: {
      conversationId: string;
      turnId: string;
      contextView: Record<string, string[]>;
    }) =>
      apiClient(
        `/api/chat/conversations/${conversationId}/turns/${turnId}/context-view`,
        {
          method: "PATCH",
          body: JSON.stringify({ context_view: contextView }),
        },
      ),

    onMutate: async ({ conversationId }) => {
      await queryClient.cancelQueries({
        queryKey: chatKeys.history(conversationId),
      });
      const previousHistory = queryClient.getQueryData<ConversationHistory>(
        chatKeys.history(conversationId),
      );
      return { previousHistory };
    },

    onError: (_error, { conversationId }, context) => {
      if (context?.previousHistory) {
        queryClient.setQueryData(
          chatKeys.history(conversationId),
          context.previousHistory,
        );
      }
    },

    onSuccess: (_data, { conversationId }) => {
      queryClient.invalidateQueries({
        queryKey: chatKeys.history(conversationId),
      });
    },
  });
}
